/**
 * 케이크 시트·토핑 데이터 정의.
 * 시트/토핑 추가 절차는 같은 폴더의 `CAKE_CUSTOMIZATION.md` 참고.
 */
export interface CakeSheetOption {
  id: string;
  label: string;
  /** 아래·중간·윗층 순 — `sheetImageUrl`이 없을 때만 화면에 쓰임(폴백·레거시) */
  layerColors: [string, string, string];
  /** 층 테두리 — 이미지 시트에서는 미사용이어도 타입상 유지 */
  strokeColor: string;
  /**
   * public 기준 경로(png/webp/svg). 있으면 미리보기는 이 이미지로 시트를 그립니다.
   * 같은 이름으로 파일만 갈아끼우면 됨: `/images/birthday-cake/sheets/{id}.svg`
   */
  sheetImageUrl?: string;
  /** 케이크 위 이름 글자를 밝은색으로(어두운 시트) */
  nameOnCakeUsesLightInk?: boolean;
  /** 시트 선택 UI에 HOT 뱃지 */
  sheetBadge?: 'hot';
}

export const CAKE_SHEETS: CakeSheetOption[] = [
  {
    id: 'chocolate',
    label: '초콜릿',
    layerColors: ['#5D4037', '#4E342E', '#3E2723'],
    strokeColor: '#2D1B14',
    sheetImageUrl: '/images/birthday-cake/sheets/Choco.png',
    nameOnCakeUsesLightInk: true,
    sheetBadge: 'hot',
  },
  {
    id: 'whipped_cream',
    label: '휩크림',
    layerColors: ['#FFFFFF', '#FAFAFA', '#F5F5F5'],
    strokeColor: '#E0E0E0',
    sheetImageUrl: '/images/birthday-cake/sheets/whipped%20cream.png',
  },
  {
    id: 'cheese',
    label: '치즈케이크',
    layerColors: ['#FFFDE7', '#FFF9C4', '#FFECB3'],
    strokeColor: '#F9A825',
    sheetImageUrl: '/images/birthday-cake/sheets/cheese.png',
  },
  {
    id: 'matcha',
    label: '말차',
    layerColors: ['#C8E6C9', '#A5D6A7', '#81C784'],
    strokeColor: '#2E7D32',
    sheetImageUrl: '/images/birthday-cake/sheets/Matcha.png',
  },
  {
    id: 'goguma',
    label: '고구마',
    layerColors: ['#FFF8E1', '#FFE0B2', '#FFCC80'],
    strokeColor: '#E65100',
    sheetImageUrl: '/images/birthday-cake/sheets/goguma.png',
  },
  {
    id: 'strawberry',
    label: '딸기',
    layerColors: ['#FFCDD2', '#F48FB1', '#EC407A'],
    strokeColor: '#AD1457',
    sheetImageUrl: '/images/birthday-cake/sheets/strawberry.png',
    sheetBadge: 'hot',
  },
  {
    id: 'choco_strawberry',
    label: '초코 딸기',
    layerColors: ['#6D4C41', '#5D4037', '#F48FB1'],
    strokeColor: '#3E2723',
    sheetImageUrl: '/images/birthday-cake/sheets/Chocostrawberry.png',
    nameOnCakeUsesLightInk: true,
    sheetBadge: 'hot',
  },
  {
    id: 'mango',
    label: '망고',
    layerColors: ['#FFF8E1', '#FFE082', '#FFCA28'],
    strokeColor: '#F57F17',
    sheetImageUrl: '/images/birthday-cake/sheets/mango.png',
  },
];

/** 레거시 `toppingSlots` 파싱 시 위치 복원용(고정 슬롯) */
export const CAKE_TOPPING_SLOT_POSITIONS: readonly { x: number; y: number }[] = [
  { x: -42, y: 28 },
  { x: 38, y: 32 },
  { x: -30, y: -8 },
  { x: 34, y: -4 },
  { x: 0, y: -42 },
  { x: -52, y: 8 },
  { x: 52, y: 12 },
  { x: -18, y: -52 },
  { x: 22, y: -48 },
  { x: 0, y: 8 },
  { x: -36, y: -36 },
  { x: 40, y: -28 },
];

export const MAX_PLACED_TOPPINGS = 12;

/** viewBox(-100,-90,200,220) 기준 — 케이크 밖으로 너무 벗어나지 않게 */
export function clampToppingViewCoords(x: number, y: number): { x: number; y: number } {
  return {
    x: Math.round(Math.min(92, Math.max(-92, x)) * 10) / 10,
    y: Math.round(Math.min(100, Math.max(-86, y)) * 10) / 10,
  };
}

export function normalizeRotationDeg(deg: number): number {
  const r = Math.round(deg * 10) / 10;
  return ((r % 360) + 360) % 360;
}

export interface PlacedTopping {
  id: string;
  x: number;
  y: number;
  /** 시계 방향(도), 기본 0 — 배치 지점(x,y) 기준 회전 */
  rotationDeg?: number;
}

/** 토핑 — 전부 PNG 토퍼, `toppingImageUrl` 필수 */
export interface CakeToppingOption {
  id: string;
  label: string;
  toppingImageUrl: string;
  /** viewBox 좌표계에서 이미지 박스 한 변(정사각), (x,y)는 이미지 하단 중앙 */
  toppingVisualSize?: number;
}

function toppingAsset(filename: string): string {
  return `/images/birthday-cake/topping/${encodeURIComponent(filename)}`;
}

/** 케이크에 올리는 토퍼(이미지) 목록 — 공개 롤링페이퍼 장식 단계에서 사용 */
export const CAKE_TOPPINGS: CakeToppingOption[] = [
  {
    id: 'strawberry_fresh_cream_cake',
    label: '딸기 생크림 케이크',
    toppingImageUrl: toppingAsset('딸기생크림케잌.png'),
    toppingVisualSize: 88,
  },
  {
    id: 'chocolate_fresh_cream_cake',
    label: '초코 생크림 케이크',
    toppingImageUrl: toppingAsset('초코생크림케잌.png'),
    toppingVisualSize: 88,
  },
];

export interface CakeDesign {
  sheetId: string;
  /** 드롭 위치(viewBox 좌표), 최대 `MAX_PLACED_TOPPINGS`개 */
  toppings: PlacedTopping[];
  nameOnCake: string;
}

export const DEFAULT_CAKE_DESIGN: CakeDesign = {
  sheetId: 'chocolate',
  toppings: [],
  nameOnCake: '',
};

export function getSheetById(id: string): CakeSheetOption {
  return CAKE_SHEETS.find((s) => s.id === id) ?? CAKE_SHEETS[0];
}

export function parseCakeDesign(raw: unknown): CakeDesign {
  if (typeof raw !== 'string' || !raw.trim()) return { ...DEFAULT_CAKE_DESIGN };
  try {
    const o = JSON.parse(raw) as Record<string, unknown>;
    const rawSheetId = typeof o.sheetId === 'string' ? o.sheetId.trim() : '';
    const normalizedSheetId = rawSheetId === 'ube' ? 'goguma' : rawSheetId;
    const sheetId =
      normalizedSheetId && CAKE_SHEETS.some((s) => s.id === normalizedSheetId)
        ? normalizedSheetId
        : DEFAULT_CAKE_DESIGN.sheetId;
    const allowedT = new Set(CAKE_TOPPINGS.map((t) => t.id));
    const toppings: PlacedTopping[] = [];

    const pushTopping = (id: string, x: number, y: number, rotationDeg?: number) => {
      if (toppings.length >= MAX_PLACED_TOPPINGS) return;
      if (!allowedT.has(id)) return;
      const c = clampToppingViewCoords(x, y);
      const r =
        rotationDeg !== undefined && Number.isFinite(rotationDeg)
          ? normalizeRotationDeg(rotationDeg)
          : undefined;
      toppings.push(r !== undefined ? { id, x: c.x, y: c.y, rotationDeg: r } : { id, x: c.x, y: c.y });
    };

    if (Array.isArray(o.toppings)) {
      for (const row of o.toppings) {
        if (!row || typeof row !== 'object') continue;
        const r = row as Record<string, unknown>;
        const id = typeof r.id === 'string' ? r.id.trim() : '';
        const xn =
          typeof r.x === 'number' && Number.isFinite(r.x)
            ? r.x
            : typeof r.x === 'string'
              ? parseFloat(r.x)
              : NaN;
        const yn =
          typeof r.y === 'number' && Number.isFinite(r.y)
            ? r.y
            : typeof r.y === 'string'
              ? parseFloat(r.y)
              : NaN;
        const rn =
          typeof r.rotationDeg === 'number' && Number.isFinite(r.rotationDeg)
            ? r.rotationDeg
            : typeof r.rotationDeg === 'string'
              ? parseFloat(r.rotationDeg)
              : typeof r.r === 'number' && Number.isFinite(r.r)
                ? r.r
                : typeof r.r === 'string'
                  ? parseFloat(r.r)
                  : undefined;
        if (!id || !Number.isFinite(xn) || !Number.isFinite(yn)) continue;
        if (!allowedT.has(id)) continue;
        pushTopping(id, xn, yn, rn !== undefined && Number.isFinite(rn) ? rn : undefined);
      }
    } else if (Array.isArray(o.toppingSlots)) {
      for (let i = 0; i < CAKE_TOPPING_SLOT_POSITIONS.length && toppings.length < MAX_PLACED_TOPPINGS; i++) {
        const v = o.toppingSlots[i];
        if (typeof v !== 'string' || !allowedT.has(v.trim())) continue;
        const pos = CAKE_TOPPING_SLOT_POSITIONS[i];
        if (!pos) continue;
        pushTopping(v.trim(), pos.x, pos.y);
      }
    } else if (Array.isArray(o.toppingIds)) {
      const ids = (o.toppingIds as unknown[]).filter(
        (x): x is string => typeof x === 'string' && allowedT.has(x.trim())
      );
      ids.slice(0, MAX_PLACED_TOPPINGS).forEach((id, i) => {
        const pos = CAKE_TOPPING_SLOT_POSITIONS[i] ?? { x: 0, y: 8 };
        pushTopping(id.trim(), pos.x, pos.y);
      });
    }

    const nameOnCake = typeof o.nameOnCake === 'string' ? o.nameOnCake.trim().slice(0, 24) : '';
    return { sheetId, toppings: toppings.slice(0, MAX_PLACED_TOPPINGS), nameOnCake };
  } catch {
    return { ...DEFAULT_CAKE_DESIGN };
  }
}

export function serializeCakeDesign(d: CakeDesign): string {
  const toppings = [...d.toppings].slice(0, MAX_PLACED_TOPPINGS).map((t) => {
    const c = clampToppingViewCoords(t.x, t.y);
    const rot = t.rotationDeg !== undefined && Number.isFinite(t.rotationDeg) ? normalizeRotationDeg(t.rotationDeg) : 0;
    const base = { id: t.id, x: c.x, y: c.y };
    return rot !== 0 ? { ...base, rotationDeg: rot } : base;
  });
  return JSON.stringify({
    sheetId: d.sheetId,
    toppings,
    nameOnCake: d.nameOnCake.trim().slice(0, 24),
  });
}
